import { createElement } from "react";
import { SoftValidationDialogContent } from "./SoftValidationDialogContent";
import { MultiWarningDialogContent } from "./MultiWarningDialogContent";
import { ReservedSlotContent } from "./ReservedSlotContent";
import { useStore } from "../../../store/store";
import { ConflictingSlot } from "../types";

type SoftValidationType = "RESERVED_SLOT" | "DUPLICATE_SERVICE" | "NO_SCHEDULE";

type SessionWarning = {
    type: string;
    message?: string;
    conflictingSlots?: Array<ConflictingSlot>;
};

type BypassParams = {
    bypass_reserved_slot_warning?: boolean;
    bypass_duplicate_service_warning?: boolean;
    bypass_no_schedule_warning?: boolean;
    bypassed_warning_reason: string;
};

const mapWarningType = (type: string | undefined): SoftValidationType | null => {
    if (!type) return null;

    const reservedSlotTypes = ["CONFLICT", "CAPACITY_REACHED"];
    if (reservedSlotTypes.some((reservedSlotType) => type.includes(reservedSlotType))) {
        return "RESERVED_SLOT";
    }
    if (type === "DUPLICATE_SERVICE_ON_SAME_DAY") {
        return "DUPLICATE_SERVICE";
    }
    if (type === "NO_TECHNICIAN_SCHEDULE") {
        return "NO_SCHEDULE";
    }

    return null;
};

const getRawMessage = (error: any) => {
    // Bulk endpoint returns error.response.data.message; the standard directus
    // format returns error.response.data.errors[0].message.
    const bulkMessage = error?.response?.data?.message;
    const directusMessage = error?.response?.data?.errors?.[0]?.message;
    return String(bulkMessage || directusMessage || "");
};

const parseWarnings = (error: any) => {
    // The backend joins every warning/hard-error into one string with "\n".
    // Confirmable warnings are self-contained JSON objects with a known type;
    // anything else (plain text) is a hard error we cannot bypass.
    const lines = getRawMessage(error)
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    const warnings: Array<SessionWarning> = [];
    const hardErrors: Array<string> = [];

    for (const line of lines) {
        try {
            const parsed = JSON.parse(line);
            if (mapWarningType(parsed?.type)) {
                warnings.push(parsed as SessionWarning);
            }
            // JSON without a recognized type is never emitted by the backend — ignore.
        } catch {
            hardErrors.push(line);
        }
    }

    return { warnings, hardErrors };
};

const surfaceHardErrors = (messages: Array<string>) => {
    useStore.getState().notifications.setNotification({
        isOpen: true,
        message: messages.join("\n"),
        severity: "error",
    });
};

const getBypassParams = (warnings: Array<SessionWarning>, reason: string): BypassParams => {
    const types = new Set(warnings.map((warning) => mapWarningType(warning.type)));
    const bypassParams: BypassParams = { bypassed_warning_reason: reason };

    if (types.has("RESERVED_SLOT")) bypassParams.bypass_reserved_slot_warning = true;
    if (types.has("DUPLICATE_SERVICE")) bypassParams.bypass_duplicate_service_warning = true;
    if (types.has("NO_SCHEDULE")) bypassParams.bypass_no_schedule_warning = true;

    return bypassParams;
};

const getWarningMeta = (warning: SessionWarning) => {
    const validationType = mapWarningType(warning.type);

    if (validationType === "RESERVED_SLOT") {
        const conflictingSlots = warning.conflictingSlots ?? [];
        return {
            title: "Aviso de Horário Reservado",
            content: createElement(ReservedSlotContent, { conflictingSlots }),
            reasonPlaceholder: "Indique o motivo para agendar neste horário reservado",
            reasonLabel: "Horário reservado",
        };
    }

    if (validationType === "NO_SCHEDULE") {
        return {
            title: "Aviso de Horário do Técnico",
            content: warning.message ?? "",
            reasonPlaceholder: "Indique o motivo para agendar sem horário definido para este serviço",
            reasonLabel: "Horário do técnico",
        };
    }

    return {
        title: "Aviso de Serviço Duplicado",
        content: warning.message ?? "",
        reasonPlaceholder: "Indique o motivo para agendar este serviço duplicado",
        reasonLabel: "Serviço duplicado",
    };
};

const getDialogConfig = (warnings: Array<SessionWarning>) => {
    if (warnings.length === 1) {
        const meta = getWarningMeta(warnings[0]);
        return {
            title: meta.title,
            contentAndActions: createElement(SoftValidationDialogContent, {
                content: meta.content,
                reasonPlaceholder: meta.reasonPlaceholder,
            }),
        };
    }

    const warningItems = warnings.map((warning) => {
        const meta = getWarningMeta(warning);
        return { content: meta.content, reasonPlaceholder: meta.reasonPlaceholder, reasonLabel: meta.reasonLabel };
    });

    return {
        title: "Avisos de Agendamento",
        contentAndActions: createElement(MultiWarningDialogContent, { warnings: warningItems }),
    };
};

export const handleSessionSoftValidation = async (
    error: any,
    entity: string,
    retryWithBypass: (bypassParams: BypassParams) => Promise<any>,
    onCancel?: () => void
): Promise<boolean> => {
    if (entity !== "sessions") return false;

    const { warnings, hardErrors } = parseWarnings(error);

    // No confirmable warnings — let the caller show its own error banner.
    if (warnings.length === 0) return false;

    // Hard errors take precedence: no bypass flag can get past them, so show
    // them and skip the modal to avoid a confirm dialog that cannot succeed.
    if (hardErrors.length > 0) {
        surfaceHardErrors(hardErrors);
        return true;
    }

    const store = useStore.getState();
    const dialogConfig = getDialogConfig(warnings);

    const result = await new Promise<{ confirmed: boolean; reason: string }>((resolve) => {
        store.reservedSlotWarning.setResolve(resolve);
        store.appDialog.createDialog({
            ...dialogConfig,
            allowSoftCancel: false,
            maxWidth: "sm",
        });
    });

    if (!result.confirmed) {
        if (onCancel) onCancel();
        return true;
    }

    const bypassParams = getBypassParams(warnings, result.reason);

    try {
        await retryWithBypass(bypassParams);
        return true;
    } catch (retryError: any) {
        // Every soft warning was bypassed, so a failure here is a hard error
        // that only surfaced once the warnings were cleared — show it.
        const { hardErrors: retryHardErrors } = parseWarnings(retryError);
        surfaceHardErrors(retryHardErrors.length ? retryHardErrors : [getRawMessage(retryError) || "Ocorreu um erro ao agendar a sessão."]);
        return true;
    }
};
